/* eslint-disable no-console */
/* eslint-disable max-len */
import { extractFileTypeFromUrl } from './commonHelpers';
import { SUPPORTED_PODCAST_FORMATS } from '../types';
import type { UploadResult } from '../types';

/**
 * Validates if a URL points to a supported podcast format
 * @param url - The URL to validate
 * @returns boolean indicating if the URL is valid for podcast
 */
export const validatePodcastUrl = (url: string): boolean =>
{
	// Extract file extension from URL
	const fileExt = extractFileTypeFromUrl(url);

	if (!fileExt || !SUPPORTED_PODCAST_FORMATS.includes(fileExt))
	{
		return false;
	}

	return true;
};

/**
 * Extracts metadata from a podcast URL
 * @param url - The podcast URL
 * @returns object with extracted metadata
 */
export const extractPodcastMetadata = (url: string): {
	title: string;
	fileType: string;
	platform: string;
} =>
{
	// Extract filename from URL for title
	const urlParts = url.split('/');
	const filename = urlParts[urlParts.length - 1];
	const title = filename.split('.')[0] || 'Untitled Podcast';

	// Extract file type
	const fileType = extractFileTypeFromUrl(url) || 'mp3';

	// Determine platform based on URL
	let platform = 'podcast_url';

	if (url.includes('soundcloud.com'))
	{
		platform = 'soundcloud';
	}
	else if (url.includes('spotify.com'))
	{
		platform = 'spotify';
	}
	else if (url.includes('anchor.fm'))
	{
		platform = 'anchor';
	}

	return {
		title,
		fileType,
		platform
	};
};

/**
 * Processes a podcast URL and returns formatted data for upload
 * @param url - The podcast URL to process
 * @returns UploadResult object with processed podcast data
 */
export const processPodcastUrl = (url: string): UploadResult =>
{
	try
	{
		const metadata = extractPodcastMetadata(url);

		return {
			url,
			source         : 'podcast_url',
			fileType       : metadata.fileType,
			title          : metadata.title,
			platform       : metadata.platform,
			isEmbedded     : true,
			readyForUpload : true,
			metadata       : {
				isDataUri : false,
				fileSize  : 0, // Will be determined by the server
				duration  : 0 // Will be determined by the server
			}
		};
	}
	catch (error)
	{
		console.error('Error processing podcast URL:', error);
		throw new Error('Failed to process podcast URL');
	}
};

/**
 * Estimates file size from URL (placeholder function)
 * @param url - The podcast URL
 * @returns estimated file size in bytes
 */
export const estimatePodcastFileSize = async (url: string): Promise<number> =>
{
	try
	{
		// Try to get file size from HEAD request
		const response = await fetch(url, { method: 'HEAD' });
		const contentLength = response.headers.get('content-length');

		if (contentLength)
		{
			return parseInt(contentLength, 10);
		}

		// Default estimate for MP3 files (assume 1MB per minute, 5 minutes average)
		return 5 * 1024 * 1024; // 5MB default
	}
	catch (error)
	{
		console.warn('Could not estimate file size:', error);

		return 5 * 1024 * 1024; // 5MB default
	}
};

/**
 * Validates podcast URL format and accessibility
 * @param url - The URL to validate
 * @returns Promise<boolean> indicating if URL is accessible
 */
export const validatePodcastUrlAccess = async (url: string): Promise<boolean> =>
{
	try
	{
		const response = await fetch(url, { method: 'HEAD' });

		return response.ok;
	}
	catch (error)
	{
		console.warn('URL accessibility check failed:', error);

		return false; // Assume accessible if check fails
	}
};